import React, { PropsWithChildren } from 'react';
import { Divider, FormControlLabel, Grid, Switch, Typography } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import {
  CHASE_LOCATION_TYPES,
  CHASE_TYPES,
  ChaseLocationType,
  ChaseType,
  COMPONENT_SIDES,
  ComponentSide,
  GABLE_STUDDING_DIRECTIONS,
  GableStuddingDirection,
  HEEL_METHODS,
  HeelMethod,
  OVERHANG_CUT_TYPES,
  OverhangCutType,
  ProfileDefaults,
  WEBBING_START_VALUES,
  WEBBING_TYPES,
  WebbingStart,
  WebbingType,
} from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { AngleField, DistanceField, NumericField } from '@paragon/material';
import { humanCase } from '@paragon/utilities';

import { SettingsControlsProps } from '../interfaces';
import { SettingsControlsPresetToolbar } from '../preset-toolbar/SettingsControlsPresetToolbar';

function SectionDivider() {
  return (
    <Grid size={12}>
      <Divider />
    </Grid>
  );
}

function SectionTitle({ children }: PropsWithChildren) {
  return (
    <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
      <Typography variant="h6" sx={{ marginTop: 1 }}>
        {children}
      </Typography>
    </Grid>
  );
}

interface Props extends SettingsControlsProps<ProfileDefaults> {
  readonly showHeader?: boolean;
  readonly showPresetControls?: boolean /** default: true */;
}

export function ProfileDefaultsControls(props: Props) {
  const handleChange =
    <K extends keyof ProfileDefaults>(key: K) =>
    (value: ProfileDefaults[K]) => {
      props.onChange({ [key]: value });
    };

  return (
    <PropertiesPanel title={props.showHeader !== false ? 'Default Profile Settings' : undefined}>
      {props.showPresetControls !== false && (
        <>
          <SettingsControlsPresetToolbar
            settings={props.settingsObject}
            settingsType="profileDefaults"
            onOverwriteSettingsFromPreset={(settings) => props.onChange(settings)}
          />
          <Divider />
        </>
      )}

      <Grid container sx={{ m: 1 }} spacing={1}>
        {/* Slopes */}
        <Grid size={6}>
          <AngleField
            label="Top Chord Slope"
            defaultDisplayFormat="slope"
            value={props.settingsObject.topChordSlope}
            onSubmit={handleChange('topChordSlope')}
          />
        </Grid>

        <Grid size={6}>
          <AngleField
            label="Bottom Chord Slope (scissor trusses)"
            defaultDisplayFormat="slope"
            value={props.settingsObject.scissorBottomChordSlope}
            onSubmit={handleChange('scissorBottomChordSlope')}
          />
        </Grid>

        {/* Overhang */}
        <Grid size={6}>
          <PropertySelectField<OverhangCutType>
            property="Overhang Cut Type"
            value={props.settingsObject.overhangCutType}
            options={OVERHANG_CUT_TYPES}
            submit={handleChange('overhangCutType')}
          />
        </Grid>

        <Grid size={6}>
          <DistanceField
            label="Overhang Distance"
            value={props.settingsObject.overhangDistance}
            onSubmit={handleChange('overhangDistance')}
          />
        </Grid>

        <SectionDivider />

        {/* Heel Settings */}
        <Grid size={3}>
          <PropertySelectField<HeelMethod>
            property="Default Heel Method"
            options={HEEL_METHODS}
            getName={humanCase}
            value={props.settingsObject.heelMethod}
            submit={handleChange('heelMethod')}
          />
        </Grid>

        <Grid size={3}>
          <DistanceField
            label="Heel Height"
            value={props.settingsObject.heelHeight}
            onSubmit={handleChange('heelHeight')}
          />
        </Grid>
        <Grid size={3}>
          <DistanceField label="Butt Cut" value={props.settingsObject.buttCut} onSubmit={handleChange('buttCut')} />
        </Grid>
        <Grid size={3}>
          <DistanceField
            label="Interior Butt Cut"
            value={props.settingsObject.interiorButtCut}
            onSubmit={handleChange('interiorButtCut')}
          />
        </Grid>

        <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            label="Run bottom chord through"
            control={
              <Switch
                checked={props.settingsObject.chordTypeRunThrough === 'Bottom'}
                onChange={(_, checked) => props.onChange({ chordTypeRunThrough: checked ? 'Bottom' : 'Top' })}
              />
            }
          />
        </Grid>

        <SectionDivider />

        <Grid size={12}>
          <DistanceField
            label="Bearing Width"
            value={props.settingsObject.bearingWidth}
            onSubmit={handleChange('bearingWidth')}
          />
        </Grid>

        <SectionDivider />

        <SectionTitle>Gable Settings</SectionTitle>

        {/* Gable Settings */}
        <Grid size={4}>
          <DistanceField
            label="Stud Spacing"
            value={props.settingsObject.gableStudSpacing}
            onSubmit={handleChange('gableStudSpacing')}
          />
        </Grid>
        <Grid size={4}>
          <PropertySelectField<GableStuddingDirection>
            property="Studding Direction"
            value={props.settingsObject.gableStuddingDirection}
            options={GABLE_STUDDING_DIRECTIONS}
            getName={humanCase}
            submit={handleChange('gableStuddingDirection')}
          />
        </Grid>
        <Grid size={4}>
          <DistanceField
            label="Minimum Stud Length"
            value={props.settingsObject.gableMinimumStudLength}
            onSubmit={handleChange('gableMinimumStudLength')}
          />
        </Grid>

        <SectionDivider />

        {/* Attic Settings */}

        <SectionTitle>Attic Settings (Beta)</SectionTitle>

        <Grid size={3}>
          <DistanceField
            label="Room Center"
            value={props.settingsObject.atticMiddle}
            onSubmit={handleChange('atticMiddle')}
          />
        </Grid>
        <Grid size={3}>
          <DistanceField
            label="Room Width"
            value={props.settingsObject.atticWidth}
            onSubmit={handleChange('atticWidth')}
          />
        </Grid>
        <Grid size={3}>
          <DistanceField
            label="Room Height"
            value={props.settingsObject.atticHeight}
            onSubmit={handleChange('atticHeight')}
          />
        </Grid>
        <Grid size={3}>
          <DistanceField
            label="Floor Depth"
            value={props.settingsObject.atticFloorDepth}
            onSubmit={handleChange('atticFloorDepth')}
          />
        </Grid>

        <Grid size={4}>
          <PropertySelectField<WebbingType>
            property="Webbing Pattern"
            value={props.settingsObject.atticWebbingType}
            options={WEBBING_TYPES}
            getName={humanCase}
            submit={handleChange('atticWebbingType')}
          />
        </Grid>

        <Grid size={4}>
          <DistanceField
            label="Horizontal Length"
            value={props.settingsObject.atticHorizontalWebLength}
            onSubmit={handleChange('atticHorizontalWebLength')}
          />
        </Grid>

        <Grid size={4}>
          <NumericField
            label="Max Run"
            value={props.settingsObject.atticMaxNumberOfWebsInARun}
            integers
            onSubmit={handleChange('atticMaxNumberOfWebsInARun')}
          />
        </Grid>

        <Grid size={6}>
          <PropertySelectField<WebbingStart>
            property="Left Webbing Start"
            value={props.settingsObject.atticLeftWebbingStart}
            options={WEBBING_START_VALUES}
            submit={handleChange('atticLeftWebbingStart')}
          />
        </Grid>
        <Grid size={6}>
          <PropertySelectField<WebbingStart>
            property="Right Webbing Start"
            value={props.settingsObject.atticRightWebbingStart}
            options={WEBBING_START_VALUES}
            submit={handleChange('atticRightWebbingStart')}
          />
        </Grid>

        <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            label="Use Single Floor Chord"
            control={
              <Switch
                checked={props.settingsObject.atticUseSingleFloorChord}
                onChange={(_, checked) => props.onChange({ atticUseSingleFloorChord: checked })}
              />
            }
          />
        </Grid>

        <SectionDivider />

        {/* Attic Chase Settings */}

        <SectionTitle>Attic Chase Settings (Beta)</SectionTitle>

        <Grid size={4}>
          <PropertySelectField<ChaseType>
            property="Chase Type"
            value={props.settingsObject.atticChaseType}
            options={CHASE_TYPES}
            getName={humanCase}
            submit={handleChange('atticChaseType')}
          />
        </Grid>
        <Grid size={4}>
          <PropertySelectField<ChaseLocationType>
            property="Chase Location"
            value={props.settingsObject.atticChaseLocationType}
            options={CHASE_LOCATION_TYPES}
            submit={handleChange('atticChaseLocationType')}
          />
        </Grid>
        <Grid size={4}>
          <DistanceField
            label="Chase Max Width"
            value={props.settingsObject.atticChaseMaxWidth}
            onSubmit={handleChange('atticChaseMaxWidth')}
          />
        </Grid>
        <Grid size={4}>
          <DistanceField
            label="Chase Min Width"
            value={props.settingsObject.atticChaseMinWidth}
            onSubmit={handleChange('atticChaseMinWidth')}
          />
        </Grid>
        <Grid size={4}>
          <DistanceField
            label="Chase Location"
            value={props.settingsObject.atticChaseLocation}
            onSubmit={handleChange('atticChaseLocation')}
          />
        </Grid>
        <Grid size={4}>
          <PropertySelectField<ComponentSide>
            property="Chase Base Side"
            value={props.settingsObject.atticChaseBaseSide}
            options={COMPONENT_SIDES}
            submit={handleChange('atticChaseBaseSide')}
          />
        </Grid>

        <SectionDivider />

        {/* Piggyback Settings */}

        <SectionTitle>Piggyback Settings</SectionTitle>

        <Grid size={4}>
          <DistanceField
            label="Purlin Thickness"
            value={props.settingsObject.piggybackPurlinThickness}
            onSubmit={handleChange('piggybackPurlinThickness')}
          />
        </Grid>

        <Grid size={8} sx={{ alignSelf: 'center', display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            label="Run End Verticals Through"
            control={
              <Switch
                checked={props.settingsObject.piggybackRunEndVerticalsThrough}
                onChange={(_, checked) => props.onChange({ piggybackRunEndVerticalsThrough: checked })}
              />
            }
          />
        </Grid>
      </Grid>
    </PropertiesPanel>
  );
}
